import { ListItemText, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { useStyling } from '../common/Theme';
import { PUNCTUATION_MARKS_CONSTANTS } from '../common/constants/constants';
import { useI18n } from '../common/i18n/I18n';
import ListActionButtons from './ListActionButtons';
import { ProductionOrganizationInfoWithKey } from './helpers/organizationUtils';

type KeyOrganizationsTileProps = {
    item: ProductionOrganizationInfoWithKey;
    onMarkClick: (org: ProductionOrganizationInfoWithKey) => void;
    onUnmarkClick: (person: ProductionOrganizationInfoWithKey) => void;
    isLoading: { id: string; isSaving: boolean; productionRoles: string[] };
    isDisabled?: boolean;
    showNoRole?: boolean;
};

export default ({
    item: prodOrg,
    onMarkClick,
    onUnmarkClick,
    isLoading,
    isDisabled = false,
    showNoRole = true
}: KeyOrganizationsTileProps) => {
    const { theme } = useStyling();
    const noRole = useI18n('production.header.keyPeople.no.role.assigned');

    const delimitedLabels = useMemo(() => {
        const { productionRoles, organizationTypes } = prodOrg ?? {};
        if (productionRoles?.length) return productionRoles;

        if (!showNoRole) {
            if (organizationTypes?.length) return [organizationTypes[0]];
            return null;
        }

        return [noRole];

    }, [
        prodOrg?.productionRoles,
        prodOrg?.organizationTypes,
        showNoRole,
        noRole]);

    const isOrganizationLoading =
        isLoading?.id === prodOrg.organizationReference?.id &&
        isEqual(isLoading?.productionRoles, prodOrg.productionRoles) &&
        isLoading.isSaving;
    const isOrganizationDisabled =
        isDisabled ||
        (isLoading?.id !== prodOrg.organizationReference?.id &&
            isLoading.isSaving &&
            isEqual(isLoading?.productionRoles, prodOrg.productionRoles));

    if (!prodOrg) return null;

    return (
        <React.Fragment key={prodOrg.organizationReference?.id}>
            <ListItemText
                primary={<Typography noWrap>{`${prodOrg.name}`}</Typography>}
                secondary={
                    delimitedLabels?.length &&
                    delimitedLabels.map((child, index) => {
                    return (
                        <Fragment key={index}>
                            {child}{' '}
                            {index + 1 < delimitedLabels.length ? (
                                ` ${PUNCTUATION_MARKS_CONSTANTS.comma} `
                            ) : (
                                <Fragment />
                            )}
                        </Fragment>
                    );
                    })
                }
            />
            <ListActionButtons
                isAdded={prodOrg?.isKey}
                prodOrg={prodOrg}
                onMarkClick={onMarkClick}
                onUnmarkClick={onUnmarkClick}
                isLoading={isOrganizationLoading}
                isDisabled={isOrganizationDisabled}
            />
        </React.Fragment>
    );
};
